import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  LoadingSpinner,
  Spacer,
  Text
} from '@homebotapp/hb-react-component-catalog'
import { SlideOnMount } from '../../../shared/Animations'
import { AnimatedCheckList, AnimatedCheckListItem } from '../../../shared/AnimatedCheckList'
import { useEffect, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { Step } from '../types'
import topographicalMap from '../../../../assets/homes/img/topographical-map.svg'
import { selectHomeFetchId } from '../../../../store/selectors/home'

import { fetchHomeDetail } from '../../../../actions/home'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'

const MSG = defineMessages({
  heading: {
    id: 'HomePriceIndexWizard.steps.analyzing.heading',
    defaultMessage: 'Crunching the numbers'
  },
  subheading: {
    id: 'HomePriceIndexWizard.steps.analyzing.subheading',
    defaultMessage: 'After this, you get to fine tune the estimate.'
  },
  yourAddress: {
    id: 'HomePriceIndexWizard.steps.analyzing.yourAddress',
    defaultMessage: 'Your address'
  },
  searching: {
    id: 'HomePriceIndexWizard.steps.analyzing.searching',
    defaultMessage: 'Searching public records'
  },
  gathering: {
    id: 'HomePriceIndexWizard.steps.analyzing.gathering',
    defaultMessage: 'Gathering home facts'
  },
  exploring: {
    id: 'HomePriceIndexWizard.steps.analyzing.exploring',
    defaultMessage: 'Exploring market trends'
  }
})

const Slide = props => <SlideOnMount interval={50} top='-80px' {...props} />

export const Analyzing = ({ onComplete }: Step) => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const homeFetchId = useAppSelector(selectHomeFetchId)
  const homeAddress = useAppSelector(state => state.home.data.address.street)

  const [searched, setSearched] = useState(false)
  const [gathered, setGathered] = useState(false)
  const [explored, setExplored] = useState(false)

  useEffect(() => {
    const searchingTimer = setTimeout(() => setSearched(true), 2000)
    const gatheringTimer = setTimeout(() => setGathered(true), 4000)
    const exploringTimer = setTimeout(() => setExplored(true), 6000)
    const fetchHomeDetailTimer = setTimeout(() => dispatch(fetchHomeDetail(homeFetchId)).then(onComplete), 6000)

    return () => {
      clearTimeout(searchingTimer)
      clearTimeout(gatheringTimer)
      clearTimeout(exploringTimer)
      clearTimeout(fetchHomeDetailTimer)
    }
  }, [])

  return (
    <>
      <Slide>
        <Heading fontWeight='normal' mb='2'>
          {intl.formatMessage(MSG.heading)}
        </Heading>
        <Text mb='0'>{intl.formatMessage(MSG.subheading)}</Text>
      </Slide>
      <Slide>
        <Card
          marginTop='12'
          _dark={{
            backgroundColor: '#2E2E2F !important'
          }}
        >
          <CardHeader borderBottomStyle='solid' borderBottomWidth='1px' borderColor='card-border'>
            <Card boxSize='24' mx='auto' mb='2'>
              <CardBody position='relative' padding='0' display='flex' alignItems='center' justifyContent='center'>
                <Box
                  width='100%'
                  height='100%'
                  _dark={{
                    backgroundColor: 'white'
                  }}
                  _light={{
                    backgroundColor: 'black'
                  }}
                  sx={{
                    maskImage: `url(${topographicalMap})`
                  }}
                />
                <LoadingSpinner variant='bot-head' size='lg' position='absolute' />
              </CardBody>
            </Card>
            <Heading as='h3' size='3xs' color='neutral.700' variant='eyebrow' mb='0'>
              {intl.formatMessage(MSG.yourAddress)}
            </Heading>
            {homeAddress && (
              <Text marginBottom='0' fontWeight='semibold' color='neutral.900'>
                {homeAddress}
              </Text>
            )}
          </CardHeader>
          <CardBody>
            <AnimatedCheckList>
              <AnimatedCheckListItem isCompleted={searched}>{intl.formatMessage(MSG.searching)}</AnimatedCheckListItem>
              <AnimatedCheckListItem isCompleted={gathered}>{intl.formatMessage(MSG.gathering)}</AnimatedCheckListItem>
              <AnimatedCheckListItem isCompleted={explored}>{intl.formatMessage(MSG.exploring)}</AnimatedCheckListItem>
            </AnimatedCheckList>
          </CardBody>
        </Card>
      </Slide>
      <Spacer />
    </>
  )
}
